import React, { useState } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { useQueryParams } from "../../utils/hooks";
import { useQuery } from "react-query";
import { useAuth } from "../../hooks/useAuth";

import queryString from "query-string";
import ReactPaginate from "react-paginate";
import { paginationOptions } from "../../utils/helpers";
import { LoaderIcon, Logo } from "../Icons";
import currency from "currency.js";
import { toast } from "react-toastify";
import { useStoreState } from "easy-peasy";
import { useNavigate } from "react-router-dom";

import restaurantImage from "../../assets/images/restaurant.png";

function groupItemsByCategory(items) {
  const groupedData = {};

  items.forEach((item) => {
    const categoryKey = item?.Cat_Name
      ? item?.Cat_Name?.toLowerCase()?.replace(/\s+/g, "_")
      : item?.Cat_Name?.replace(/\s+/g, "_");

    if (!groupedData[categoryKey]) {
      groupedData[categoryKey] = [];
    }

    groupedData[categoryKey].push({
      name: item.Item_Name,
      price:
        item.currency === "USD"
          ? `${currency(item.UnitPrice, {
              symbol: "$",
              precision: 2,
            }).format()}`
          : `${currency(item.UnitPrice, {
              symbol: "₦",
              precision: 2,
            }).format()}`,
      barCode: item.Bar_Code,
    });
  });

  return groupedData;
}

// Helper function to get category display name
function getCategoryDisplayName(categoryKey) {
  return categoryKey
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getCategoryIcon(categoryKey) {
  // Array of food and drink related emojis
  const foodEmojiOptions = [
    "🍽️",
    "🥤",
    "🍺",
    "🍷",
    "🍸",
    "☕",
    "🍵",
    "🍞",
    "🍩",
    "🍕",
    "🍔",
    "🥗",
    "🍟",
    "🌭",
    "🥪",
    "🌮",
    "🌯",
    "🫔",
    "🥙",
    "🧆",
    "🍖",
    "🍗",
    "🥩",
    "🥓",
    "🍳",
    "🧈",
    "🥞",
    "🧇",
    "🍤",
    "🍣",
    "🍱",
    "🍜",
    "🍲",
    "🫕",
    "🥘",
    "🍛",
    "🍚",
    "🍙",
    "🍘",
    "🍥",
    "🥠",
    "🥮",
    "🍢",
    "🍡",
    "🍧",
    "🍨",
    "🍦",
    "🥧",
    "🧁",
    "🍰",
    "🎂",
    "🍮",
    "🍭",
    "🍬",
    "🍫",
    "🍿",
    "🍪",
    "🥜",
    "🌰",
    "🥨",
  ];

  // Create a simple hash from the category key to ensure consistency
  // Same category will always get the same emoji
  let hash = 0;
  for (let i = 0; i < categoryKey.length; i++) {
    const char = categoryKey.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Use absolute value and modulo to get a consistent index
  const index = Math.abs(hash) % foodEmojiOptions.length;

  return foodEmojiOptions[index];
}

const MenuPage = () => {
  const navigate = useNavigate();
  const initialFilterParams = {
    page: 1,
    limit: 150, // Increased to get more items
  };
  const [queryParams] = useQueryParams({
    page: 1,
    limit: 150,
    ...initialFilterParams,
  });

  const { customerBackendUrl: backendUrl } = useAuth();

  const { data, isFetching } = useQuery(
    [queryParams],
    () => getItems(queryParams),
    {
      keepPreviousData: true,
    }
  );
  const fetchSetUpData = async () => {
    // await waitFor(5000);
    let response = await fetch(`${backendUrl}/api/store/set-up`, {
      method: "GET",
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();

    return data;
  };

  const { data: settings, isLoading } = useQuery(
    ["STORE_NAV_SET_UP"],
    () => fetchSetUpData(),
    {
      keepPreviousData: true,
    }
  );
  if (!isLoading && !settings?.settings?.enableMenuInOnlineStore) {
    return (
      <div
        className="vw-100 d-flex align-items-center justify-content-center text-center "
        style={{ height: "60vh" }}
      >
        <div>
          <Logo />
          <h4 className="display-5"> Unauthorized</h4>{" "}
          <Button
            onClick={() => navigate(-1)}
            variant=""
            className="text-primary text-underline"
          >
            Go back
          </Button>
        </div>
      </div>
    );
  }

  const getItems = async (debouncedqueryParams) => {
    let response = await fetch(
      `${backendUrl}/api/items?${queryString.stringify(debouncedqueryParams)}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();

    const result = groupItemsByCategory(data?.items || []);
    data.menuData = result;

    return data;
  };

  // Use dynamic menu data or fallback to empty object
  const menuData = data?.menuData;

  const MenuItem = ({ item }) => (
    <div className="d-flex align-items-center mb-3">
      <span
        className="text-muted"
        style={{ minWidth: "120px", fontSize: "16px" }}
      >
        {item.name}
      </span>
      <div
        className="flex-fill mx-3"
        style={{
          height: "1px",
          backgroundImage:
            "repeating-linear-gradient(to right, #999 0, #999 3px, transparent 3px, transparent 8px)",
        }}
      />
      <span
        className="fw-semibold text-dark"
        style={{ minWidth: "40px", textAlign: "right", fontSize: "16px" }}
      >
        {item.price}
      </span>
    </div>
  );

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: data?.company?.CompName,
        text: `Check out our menu at ${data?.company?.CompName}!`,
        url: window.location.href,
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(window.location.href).then(() => {
        toast.success("Link copied to clipboard!");
      });
    }
  };

  const MenuSection = ({ title, items, icon }) => (
    <div className="mb-5">
      <div className="d-flex align-items-center mb-3">
        <h3
          className="fw-semibold text-dark me-3 mb-0"
          style={{ fontSize: "28px" }}
        >
          {title}
        </h3>
        <span style={{ fontSize: "22px", color: "#666" }}>{icon}</span>
      </div>
      <div>
        {items.map((item, index) => (
          <MenuItem key={index} item={item} />
        ))}
      </div>
    </div>
  );

  // Loading state
  if (isFetching && !data) {
    return (
      <div className="global-spinner">
        <LoaderIcon className="spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <style jsx>{`
        .layout-container {
          display: flex;
          height: 100vh;
        }

        .left-panel-static {
          width: 33.333333%;
          height: 100vh;
          overflow: hidden;
          flex-shrink: 0;
        }

        .right-panel-scrollable {
          width: 66.666667%;
          height: 100vh;
          overflow-y: auto;
          flex-shrink: 0;
        }

        .share-btn {
          position: absolute;
          top: 20px;
          right: 20px;
          z-index: 1001;
          border: none;
          border-radius: 50%;
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }

        .share-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
        }

        .geometric-shape {
          position: absolute;
          opacity: 0.15;
          font-weight: bold;
          pointer-events: none;
        }

        .left-shapes .geometric-shape {
          color: #444;
          opacity: 0.2;
        }

        .right-shapes .geometric-shape {
          color: #333;
          opacity: 0.15;
        }

        .shape-star-1 {
          top: 15%;
          left: 15%;
          font-size: 20px;
        }

        .shape-triangle-1 {
          top: 25%;
          right: 20%;
          font-size: 24px;
        }

        .shape-diamond-1 {
          bottom: 40%;
          left: 20%;
          font-size: 18px;
        }

        .shape-star-2 {
          bottom: 20%;
          left: 60%;
          font-size: 16px;
        }

        .shape-star-3 {
          top: 8%;
          right: 15%;
          font-size: 20px;
        }

        .shape-triangle-2 {
          top: 35%;
          right: 8%;
          font-size: 22px;
        }

        .shape-diamond-2 {
          bottom: 45%;
          right: 12%;
          font-size: 18px;
        }

        .shape-star-4 {
          bottom: 15%;
          right: 20%;
          font-size: 16px;
        }

        .shape-oval-1 {
          top: 60%;
          left: 5%;
          font-size: 14px;
          transform: rotate(45deg);
        }

        .shape-oval-2 {
          bottom: 8%;
          left: 15%;
          font-size: 16px;
          transform: rotate(-20deg);
        }

        .empty-menu {
          text-align: center;
          padding: 60px 20px;
          color: #666;
        }

        .empty-menu h3 {
          margin-bottom: 10px;
          color: #999;
        }
      `}</style>

      <div
        className="layout-container"
        style={{
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        }}
      >
        {/* Static Left Panel */}
        <div
          className="left-panel-static bg-dark d-flex flex-column justify-content-between position-relative"
          // style={{ background: "#000" }}
          style={{
            backgroundImage: `url(${restaurantImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.4)",
              zIndex: 1,
            }}
          />
          <div
            className="d-flex flex-column justify-content-between position-relative"
            style={{ height: "100%", justifyContent: "space-between" }}
          >
            <div className="p-4 pt-5">
              <h1
                className="text-white fw-bold mb-0"
                style={{ fontSize: "48px", lineHeight: "1.1" }}
              >
                {data?.company?.CompName}
              </h1>
            </div>

            <div className="p-4 pb-4 fixed-bottom bottom-0">
              <div
                className="text-white"
                style={{ fontSize: "14px", lineHeight: "1.4" }}
              >
                <div className="mb-1"> {data?.company?.AddressLine1}</div>
                <div className="mb-1">{data?.company?.AddressLine2}</div>
                <div>{data?.company?.Phone}</div>
              </div>
            </div>
          </div>

          <div className="left-shapes">
            <div className="geometric-shape shape-star-1">
              <img
                src={`${backendUrl}/api/users/logo/company`}
                alt=""
                width={100}
                height={100}
              />{" "}
              ✱
            </div>
            <div className="geometric-shape shape-triangle-1">
              ▲{" "}
              <img
                src={`${backendUrl}/api/users/logo/company`}
                alt=""
                width={100}
                height={100}
              />{" "}
            </div>
            <div className="geometric-shape shape-diamond-1">
              ◆{" "}
              <img
                src={`${backendUrl}/api/users/logo/company`}
                alt=""
                width={100}
                height={100}
              />{" "}
            </div>
            <div className="geometric-shape shape-star-2">
              {" "}
              <img
                src={`${backendUrl}/api/users/logo/company`}
                alt=""
                width={100}
                height={100}
              />{" "}
              ✱
            </div>
          </div>
        </div>

        {/* Scrollable Right Panel */}
        <div
          className="right-panel-scrollable position-relative"
          style={{ background: "#f8f8f8" }}
        >
          {/* Share Button */}
          <Button
            className="share-btn"
            onClick={handleShare}
            title="Share Menu"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
          </Button>

          <div className="right-shapes">
            <div className="geometric-shape shape-star-3">
              {" "}
              <img
                src={`${backendUrl}/api/users/logo/company`}
                alt=""
                width={100}
                height={100}
              />{" "}
              ✱
            </div>
            <div className="geometric-shape shape-triangle-2">
              {" "}
              <img
                src={`${backendUrl}/api/users/logo/company`}
                alt=""
                width={100}
                height={100}
              />{" "}
              ▲
            </div>
            <div className="geometric-shape shape-diamond-2">
              ◆{" "}
              <img
                src={`${backendUrl}/api/users/logo/company`}
                alt=""
                width={100}
                height={100}
              />{" "}
            </div>
            <div className="geometric-shape shape-star-4">
              {" "}
              <img
                src={`${backendUrl}/api/users/logo/company`}
                alt=""
                width={100}
                height={100}
              />{" "}
              ✱
            </div>
            <div className="geometric-shape shape-oval-1">
              ⬬{" "}
              <img
                src={`${backendUrl}/api/users/logo/company`}
                alt=""
                width={100}
                height={100}
              />{" "}
            </div>
            <div className="geometric-shape shape-oval-2">
              ⬬{" "}
              <img
                src={`${backendUrl}/api/users/logo/company`}
                alt=""
                width={100}
                height={100}
              />{" "}
            </div>
          </div>

          <div className="p-4 p-md-5">
            <div
              className="d-flex align-items-center mb-5"
              style={{ paddingTop: "40px" }}
            >
              <h2
                className="fw-bold text-dark me-3 mb-0"
                style={{ fontSize: "48px" }}
              >
                Menu
              </h2>
              <span style={{ fontSize: "24px", color: "#666" }}>🍴</span>
            </div>

            {/* Dynamic Menu Sections */}
            {Object.keys(menuData).length > 0 ? (
              Object.entries(menuData).map(([categoryKey, items]) => {
                const displayName = getCategoryDisplayName(categoryKey);
                return (
                  <MenuSection
                    key={categoryKey}
                    title={displayName}
                    items={items}
                    icon={getCategoryIcon(categoryKey, displayName)}
                  />
                );
              })
            ) : (
              <div className="empty-menu">
                <h3>No menu items available</h3>
                <p>
                  Please check back later or contact us for more information.
                </p>
              </div>
            )}

            {/* Loading indicator for updates */}
            {isFetching && data && (
              <div className="global-spinner">
                <LoaderIcon className="spin text-primary" />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MenuPage;
